"use client";

import { useMemo, useState } from "react";
import { ChevronLeft, Search } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import {
  getEnabledIndicatorCategories,
  type IndicatorCategoryView,
} from "@/lib/chart-indicators/registry";
import type { CatalogEntry } from "@/lib/chart-indicators/catalog";

interface AddIndicatorScreenProps {
  onBack: () => void;
  onSelectIndicator: (indicator: CatalogEntry) => void;
  isDesktop?: boolean;
}

type CategoryFilter = "all" | IndicatorCategoryView["id"];

export default function IndicatorAddScreen({
  onBack,
  onSelectIndicator,
  isDesktop = false,
}: AddIndicatorScreenProps) {
  const categories = useMemo(() => getEnabledIndicatorCategories(), []);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        indicators: cat.indicators.filter((ind) => {
          if (categoryFilter !== "all" && cat.id !== categoryFilter) return false;
          if (!q) return true;
          return (
            ind.name.toLowerCase().includes(q) ||
            ind.indicatorId.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((cat) => cat.indicators.length > 0);
  }, [categories, search, categoryFilter]);

  const chips: { id: CategoryFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "trend", label: "Trend" },
    { id: "oscillators", label: "Oscillators" },
    { id: "volumes", label: "Volumes" },
    { id: "bill-williams", label: "Bill Williams" },
  ];

  return (
    <div className="h-full flex flex-col min-h-0 bg-background">
      <div
        className={`flex items-center gap-2 border-b border-border shrink-0 ${
          isDesktop ? "px-3 py-2 h-10" : "px-4 py-3 h-14"
        }`}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className={isDesktop ? "h-7 w-7" : "h-10 w-10"}
        >
          <ChevronLeft className={isDesktop ? "h-4 w-4" : "h-5 w-5"} />
        </Button>
        <h2 className={`font-medium flex-1 ${isDesktop ? "text-sm" : "text-lg"}`}>
          Add indicator
        </h2>
      </div>

      <div className="px-3 py-2 space-y-2 shrink-0 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search indicators…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <Button
              key={chip.id}
              type="button"
              size="sm"
              variant={categoryFilter === chip.id ? "default" : "outline"}
              className="h-7 text-xs px-2.5"
              onClick={() => setCategoryFilter(chip.id)}
            >
              {chip.label}
            </Button>
          ))}
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 space-y-4">
          {filteredCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No indicators match your search.
            </p>
          ) : (
            filteredCategories.map((category) => (
              <section key={category.id}>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 px-1">
                  {category.name}
                </h3>
                <ul className="rounded-lg border border-border divide-y divide-border overflow-hidden">
                  {category.indicators.map((indicator) => (
                    <li key={indicator.indicatorId}>
                      <button
                        type="button"
                        onClick={() => onSelectIndicator(indicator)}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-left text-sm hover:bg-muted/60 transition-colors"
                      >
                        <span className="font-medium">{indicator.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {indicator.createsNewChart ? "Separate pane" : "Main chart"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
